/**
 * Fixed content the default factories draw on: word lists, decodable passages,
 * the classic tangram dissection, and the part names each 3D model exposes.
 *
 * All of it is data, not logic — it stays here so the schemas stay readable.
 */

import type { NormalizedPoint } from './common'

// ─────────────────────────────────────────────────────────────
// Reading
// ─────────────────────────────────────────────────────────────

/**
 * High-frequency words in rough teaching order. The Cycle-2 method is volume
 * and repetition at an easy level, so the deck starts with the words that pay
 * back fastest.
 */
export const SIGHT_WORDS: readonly string[] = [
  'the', 'a', 'and', 'is', 'it', 'in', 'to', 'I', 'you', 'we',
  'go', 'see', 'look', 'my', 'me', 'he', 'she', 'up', 'down', 'can',
  'on', 'at', 'am', 'like', 'said', 'was', 'they', 'for', 'have', 'has',
  'this', 'that', 'with', 'come', 'here', 'play', 'run', 'big', 'little', 'help',
  'get', 'make', 'find', 'jump', 'stop', 'yes', 'no', 'not', 'all', 'are',
  'do', 'did', 'out', 'day', 'one', 'two', 'three', 'her', 'him', 'them',
]

/**
 * Repeated-reading texts. Deliberately easy: at a fluency wall, dropping the
 * difficulty and piling up repetition is what makes reading automatic.
 */
export const READING_PASSAGES: Record<3 | 4 | 5, string> = {
  3: 'The cat sat on the mat. The cat had a hat. The hat was red. The cat ran to me.',
  4: 'Sam has a big red bag. In the bag is a snack. Sam sits on the step and eats the snack. A duck runs up to Sam. Sam gives the duck a bit of the snack. The duck is glad.',
  5: 'Nell went to the pond to look for frogs. The grass was wet and the wind was swift. She sat still on a flat rock and did not make a sound. At last a small green frog swam up. It sat on a leaf and blinked at her. Nell went home and drew the frog so she could show it to her mum.',
}

/** Rough words per line, used to size a target time honestly. */
export function countWords(text: string): number {
  return text.split(/\s+/).filter((word) => word.length > 0).length
}

// ─────────────────────────────────────────────────────────────
// Tangram
// ─────────────────────────────────────────────────────────────

export interface TangramPieceGeometry {
  id: string
  shape: 'large_triangle' | 'medium_triangle' | 'small_triangle' | 'square' | 'parallelogram'
  colorToken: string
  /** Polygon at its SOLVED position in the unit target square. */
  polygon: NormalizedPoint[]
}

const point = (x: number, y: number): NormalizedPoint => ({ x, y })

/**
 * The classic seven-piece dissection of the unit square. Areas are
 * 4:4:2:2:2:1:1 out of 16 — the real thing, not an approximation.
 */
export const CLASSIC_TANGRAM_PIECES: readonly TangramPieceGeometry[] = [
  {
    id: 'large-triangle-a',
    shape: 'large_triangle',
    colorToken: 'piece.coral',
    polygon: [point(0, 0), point(1, 0), point(0.5, 0.5)],
  },
  {
    id: 'large-triangle-b',
    shape: 'large_triangle',
    colorToken: 'piece.amber',
    polygon: [point(0, 0), point(0.5, 0.5), point(0, 1)],
  },
  {
    id: 'medium-triangle',
    shape: 'medium_triangle',
    colorToken: 'piece.teal',
    polygon: [point(1, 0.5), point(1, 1), point(0.5, 1)],
  },
  {
    id: 'square',
    shape: 'square',
    colorToken: 'piece.violet',
    polygon: [point(0.5, 0.5), point(0.75, 0.25), point(1, 0.5), point(0.75, 0.75)],
  },
  {
    id: 'parallelogram',
    shape: 'parallelogram',
    colorToken: 'piece.rose',
    polygon: [point(0, 1), point(0.5, 1), point(0.75, 0.75), point(0.25, 0.75)],
  },
  {
    id: 'small-triangle-a',
    shape: 'small_triangle',
    colorToken: 'piece.sky',
    polygon: [point(1, 0), point(1, 0.5), point(0.75, 0.25)],
  },
  {
    id: 'small-triangle-b',
    shape: 'small_triangle',
    colorToken: 'piece.lime',
    polygon: [point(0.75, 0.75), point(0.25, 0.75), point(0.5, 0.5)],
  },
]

/** The square silhouette every classic set solves into. */
export const TANGRAM_SQUARE_OUTLINE: readonly NormalizedPoint[] = [
  point(0, 0),
  point(1, 0),
  point(1, 1),
  point(0, 1),
]

/** Where each piece waits before it is placed, as an offset from its solved spot. */
export const TANGRAM_TRAY_OFFSETS: Record<string, { dx: number; dy: number; rotationDeg: number }> = {
  'large-triangle-a': { dx: -0.15, dy: 0.55, rotationDeg: 180 },
  'large-triangle-b': { dx: 0.6, dy: 0.2, rotationDeg: 90 },
  'medium-triangle': { dx: -0.55, dy: -0.35, rotationDeg: 270 },
  square: { dx: -0.35, dy: 0.35, rotationDeg: 45 },
  parallelogram: { dx: 0.35, dy: -0.6, rotationDeg: 180 },
  'small-triangle-a': { dx: -0.7, dy: 0.25, rotationDeg: 90 },
  'small-triangle-b': { dx: 0.2, dy: -0.5, rotationDeg: 135 },
}

// ─────────────────────────────────────────────────────────────
// 3D models
// ─────────────────────────────────────────────────────────────

export const MODEL_TYPES = ['earth_sun_day_night', 'solar_system', 'body_systems'] as const
export type ModelType = (typeof MODEL_TYPES)[number]

export const BODY_SYSTEMS = ['skeleton', 'heart_lungs', 'digestive'] as const
export type BodySystem = (typeof BODY_SYSTEMS)[number]

export const PLANETS = [
  'mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune',
] as const

/** Only these part keys can carry a label — a label on a part that is not in the scene is a bug. */
export const MODEL_PARTS: Record<ModelType, readonly string[]> = {
  earth_sun_day_night: ['sun', 'earth', 'axis', 'day_side', 'night_side', 'moon'],
  solar_system: ['sun', ...PLANETS, 'orbit_path', 'asteroid_belt'],
  body_systems: [
    'skull', 'ribs', 'spine', 'arm_bones', 'leg_bones',
    'heart', 'lungs', 'windpipe', 'blood_vessels',
    'mouth', 'stomach', 'intestines', 'liver',
  ],
}

export const BODY_SYSTEM_PARTS: Record<BodySystem, readonly string[]> = {
  skeleton: ['skull', 'ribs', 'spine', 'arm_bones', 'leg_bones'],
  heart_lungs: ['heart', 'lungs', 'windpipe', 'blood_vessels'],
  digestive: ['mouth', 'stomach', 'intestines', 'liver'],
}

export const MODEL_LABEL_TEXT: Record<string, string> = {
  sun: 'the Sun',
  earth: 'Earth',
  axis: 'the tilt',
  day_side: 'daytime side',
  night_side: 'night side',
  moon: 'the Moon',
  mercury: 'Mercury',
  venus: 'Venus',
  mars: 'Mars',
  jupiter: 'Jupiter',
  saturn: 'Saturn',
  uranus: 'Uranus',
  neptune: 'Neptune',
  orbit_path: 'the path it travels',
  asteroid_belt: 'the asteroid belt',
  skull: 'skull',
  ribs: 'ribs',
  spine: 'spine',
  arm_bones: 'arm bones',
  leg_bones: 'leg bones',
  heart: 'heart',
  lungs: 'lungs',
  windpipe: 'windpipe',
  blood_vessels: 'blood vessels',
  mouth: 'mouth',
  stomach: 'stomach',
  intestines: 'intestines',
  liver: 'liver',
}
